/**
 * Detailed Sales Report Page
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download, FileText, Search } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, formatClientWithNote, formatClientWithNoteForExport, formatAmountForExport, debounce } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { t as i18nT } from '@/i18n'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { DatePicker } from '@/components/ui/date-picker'
import { Pagination } from '@/components/ui/pagination'

const PAGE_SIZE = 50

interface DetailedSaleItem {
  bill_id: number
  bill_number: string | null
  consecutivo_hacienda: string | null
  hacienda_status: string | null
  bill_date: string
  article_id: number
  article_code: string
  article_cabys: string | null
  article_description: string
  document_type?: string
  quantity: number
  unit_price: number
  unit_price_with_tax: number
  line_discount: number
  line_total: number
  line_tax: number
  line_total_with_tax: number
  client_name: string | null
  client_note?: string | null
}

// Las notas de crédito salen en negativo; el tipo las distingue en pantalla y en el Excel.
const documentKindLabel = (type?: string) =>
  type === 'credit_note' ? i18nT('billing.creditNote') : type === 'ticket' ? i18nT('billing.ticket') : i18nT('billing.invoice')

interface DetailedSalesData {
  items: DetailedSaleItem[]
  page: number
  page_size: number
  total_items: number
  pages: number
  totals: {
    subtotal: number
    total_tax: number
    total_discount: number
    total_amount: number
    total_with_tax: number
    cash_total: number
    credit_total: number
    items_count: number
  }
}

export function DetailedSalesReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const haciendaStatusLabel = (status: string | null): string => {
    switch (status) {
      case 'accepted':
        return t('reports.haciendaAccepted')
      case 'rejected':
        return t('reports.haciendaRejected')
      case 'failed':
        return t('reports.haciendaFailed')
      case 'submitted':
        return t('reports.haciendaSubmitted')
      case 'pending':
        return t('reports.haciendaPending')
      default:
        return t('reports.haciendaNotSent')
    }
  }

  const [dateFrom, setDateFrom] = useUrlState('desde')
  const [dateTo, setDateTo] = useUrlState('hasta')
  const [searchQuery, setSearchQuery] = useUrlState('q')
  const [searchInput, setSearchInput] = useState(searchQuery)
  const [payType, setPayType] = useUrlState('pago')
  const [haciendaFilter, setHaciendaFilter] = useUrlState('hacienda')
  const [page, setPage] = useUrlPage()
  const [exportRequested, setExportRequested] = useState(false)

  const hasDateFilter = !!(dateFrom || dateTo)
  const hasAnyFilter = hasDateFilter || !!searchQuery || !!payType || !!haciendaFilter

  // El buscador escribe con retardo: sin esto cada tecla dispara una consulta.
  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchQuery(value), 350),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Cualquier cambio de filtro vuelve a la primera página (lo hace useUrlState):
  // quedarse en la 7 de un resultado que ahora tiene 2 muestra una tabla vacía.
  useEffect(() => {
    setExportRequested(false)
  }, [dateFrom, dateTo, searchQuery, payType, haciendaFilter])

  const filterParams = (extra?: Record<string, string>) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', `${dateTo}T23:59:59`)
    if (searchQuery) params.set('search', searchQuery)
    if (payType) params.set('pay_type', payType)
    if (haciendaFilter) params.set('hacienda_status', haciendaFilter)
    Object.entries(extra || {}).forEach(([k, v]) => params.set(k, v))
    return params.toString()
  }

  const { data: detailedSales, isLoading } = useQuery({
    queryKey: ['reports-detailed-sales', dateFrom, dateTo, searchQuery, payType, haciendaFilter, page],
    queryFn: async () => {
      const qs = filterParams({ page: String(page), page_size: String(PAGE_SIZE) })
      const response = await api.get(`/reports/sales/detailed?${qs}`)
      return response.data as DetailedSalesData
    },
  })

  // El export baja TODO lo filtrado, no la página visible. Es perezoso: se
  // dispara al tocar Exportar, no al abrir el reporte, porque un mes típico
  // son ~1 500 líneas que no vale la pena traer en cada visita.
  const { data: exportData, isFetching: isExporting } = useQuery({
    queryKey: ['reports-detailed-sales-export', dateFrom, dateTo, searchQuery, payType, haciendaFilter],
    queryFn: async () => {
      const response = await api.get(`/reports/sales/detailed?${filterParams({ all: 'true' })}`)
      return response.data as DetailedSalesData
    },
    enabled: exportRequested,
  })

  const exportItems = exportData?.items ?? []

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.detailedSales')}</h1>
          <p className="text-sm text-muted-foreground">
            {hasDateFilter ? t('reports.dateRange') : t('reports.thisMonth')}
          </p>
        </div>
        {exportItems.length > 0 ? (
          <ExportButtons
            data={exportItems.map((it) => ({
              ...it,
              client_name: formatClientWithNoteForExport(it.client_name, it.client_note),
            }))}
            filename="ventas-detalladas"
            columns={[
              { key: 'bill_number', header: t('reports.invoiceNumber'), asText: true },
              { key: 'consecutivo_hacienda', header: t('reports.consecutivoHacienda'), asText: true },
              { key: 'document_type', header: t('reports.documentKind'), format: (v) => documentKindLabel(v as string | undefined) },
              { key: 'hacienda_status', header: t('reports.haciendaStatus'), format: (v) => haciendaStatusLabel(v as string | null) },
              { key: 'bill_date', header: t('reports.date'), format: (v) => formatDate(v as string) },
              { key: 'article_code', header: t('articles.code'), asText: true },
              { key: 'article_cabys', header: t('articles.cabysCode'), asText: true },
              { key: 'article_description', header: t('articles.description') },
              { key: 'quantity', header: t('reports.qty'), format: (v) => String(v) },
              { key: 'unit_price', header: t('reports.unitPriceShort'), format: (v) => formatAmountForExport(v as number) },
              { key: 'unit_price_with_tax', header: t('reports.unitPriceWithTaxShort'), format: (v) => formatAmountForExport(v as number) },
              { key: 'line_discount', header: t('reports.discountShort'), format: (v) => formatAmountForExport(v as number), total: true },
              { key: 'line_total', header: t('reports.total'), format: (v) => formatAmountForExport(v as number), total: true },
              { key: 'line_tax', header: t('reports.lineTax'), format: (v) => formatAmountForExport(v as number), total: true },
              { key: 'line_total_with_tax', header: t('reports.totalWithTax'), format: (v) => formatAmountForExport(v as number), total: true },
              { key: 'client_name', header: t('clients.name') },
            ]}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-10 shrink-0"
            onClick={() => setExportRequested(true)}
            disabled={isExporting || !detailedSales?.total_items}
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">
              {isExporting ? t('common.loading') : t('export.export')}
            </span>
          </Button>
        )}
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9 h-10"
          placeholder={t('reports.searchSalesPlaceholder')}
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={payType}
          onChange={(e) => setPayType(e.target.value)}
          aria-label={t('reports.allPayTypes')}
        >
          <option value="">{t('reports.allPayTypes')}</option>
          <option value="cash">{t('reports.onlyCash')}</option>
          <option value="credit">{t('reports.onlyCredit')}</option>
        </select>
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={haciendaFilter}
          onChange={(e) => setHaciendaFilter(e.target.value)}
          aria-label={t('reports.allHaciendaStatuses')}
        >
          <option value="">{t('reports.allHaciendaStatuses')}</option>
          <option value="accepted">{t('reports.haciendaAccepted')}</option>
          <option value="rejected">{t('reports.haciendaRejected')}</option>
          <option value="submitted">{t('reports.haciendaSubmitted')}</option>
          <option value="pending">{t('reports.haciendaPending')}</option>
          <option value="failed">{t('reports.haciendaFailed')}</option>
          <option value="none">{t('reports.haciendaNotSent')}</option>
        </select>
        <DatePicker
          value={dateFrom}
          onChange={setDateFrom}
          max={dateTo || undefined}
          placeholder={t('reports.from')}
          ariaLabel={t('reports.from')}
        />
        <DatePicker
          value={dateTo}
          onChange={setDateTo}
          min={dateFrom || undefined}
          placeholder={t('reports.to')}
          ariaLabel={t('reports.to')}
        />
      </div>

      {/* Summary Cards */}
      {detailedSales?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{t('reports.subtotal')}</p>
              <p className="text-lg font-bold">{formatCurrency(detailedSales.totals.subtotal)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{t('reports.totalTax')}</p>
              <p className="text-lg font-bold">{formatCurrency(detailedSales.totals.total_tax)}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{t('reports.totalWithTax')}</p>
              <p className="text-lg font-bold">{formatCurrency(detailedSales.totals.total_with_tax)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{t('reports.cashSales')}</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(detailedSales.totals.cash_total)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{t('reports.creditSales')}</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(detailedSales.totals.credit_total)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-500" />
            {t('reports.lineDetail')} (
            {t('reports.linesCount', { count: detailedSales?.total_items ?? 0 })})
          </CardTitle>
          {(detailedSales?.pages ?? 1) > 1 && (
            <p className="text-xs text-muted-foreground">
              {t('reports.totalsCoverAllFilters')}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : detailedSales?.items && detailedSales.items.length > 0 ? (
            <div className="space-y-2">
              {detailedSales.items.map((item, idx) => (
                <div
                  key={`${item.bill_id}-${item.article_id}-${idx}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {item.article_code} - {item.article_description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {documentKindLabel(item.document_type)}: {item.consecutivo_hacienda || item.bill_number || item.bill_id} - {formatDate(item.bill_date)}
                      {item.client_name && ` - ${formatClientWithNote(item.client_name, item.client_note)}`}
                    </p>
                    <span
                      className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        item.hacienda_status === 'accepted'
                          ? 'bg-green-100 text-green-700'
                          : item.hacienda_status === 'rejected' || item.hacienda_status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {haciendaStatusLabel(item.hacienda_status)}
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold">{formatCurrency(item.line_total)}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.quantity} x {formatCurrency(item.unit_price)}
                    </p>
                  </div>
                </div>
              ))}
              {detailedSales.pages > 1 && (
                <div className="flex flex-col items-center gap-2 pt-4">
                  <p className="text-sm text-muted-foreground">
                    {t('common.page')} {detailedSales.page} {t('common.of')} {detailedSales.pages}
                  </p>
                  <Pagination
                    currentPage={page}
                    totalPages={detailedSales.pages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {hasAnyFilter ? t('reports.noResultsWithFilters') : t('reports.noDataAvailable')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
